import { useEffect, useMemo, useRef, useState } from 'react';
import { FaPen } from 'react-icons/fa';
import { useAboutContent } from '../../ViewModel/AboutViewModel.js';
import '../styles/About.css';

function HomeTimelineSection({ adminMode = false }) {
  const { content, loading, saving, saveContent } = useAboutContent();
  const [expandedTimeline, setExpandedTimeline] = useState({});
  const [timelineDraft, setTimelineDraft] = useState([]);
  const [editingTimeline, setEditingTimeline] = useState(false);
  const [activeEditor, setActiveEditor] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const editorRef = useRef(null);

  const safeTimeline = useMemo(
    () => (Array.isArray(timelineDraft) ? timelineDraft : []),
    [timelineDraft]
  );

  const timelineWithStep = useMemo(
    () => safeTimeline.map((step, idx) => ({ ...step, step: idx + 1 })),
    [safeTimeline]
  );

  const toggleTimelineExpand = (index) => {
    setExpandedTimeline((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  useEffect(() => {
    setTimelineDraft(Array.isArray(content?.timeline) ? content.timeline : []);
  }, [content?.timeline]);

  useEffect(() => {
    if (!activeEditor) return undefined;

    const timeoutId = setTimeout(() => {
      setActiveEditor(false);
    }, 1800);

    return () => clearTimeout(timeoutId);
  }, [activeEditor]);

  const openEditor = () => {
    setEditingTimeline(true);
    setActiveEditor(true);

    setTimeout(() => {
      const section = editorRef.current;
      if (!section) return;

      section.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const firstField = section.querySelector('input, textarea, button');
      if (firstField && typeof firstField.focus === 'function') {
        firstField.focus({ preventScroll: true });
      }
    }, 80);
  };

  const cancelTimeline = () => {
    setTimelineDraft(Array.isArray(content?.timeline) ? content.timeline : []);
    setEditingTimeline(false);
    setActiveEditor(false);
  };

  const addListItem = () => {
    setTimelineDraft((prev) => [...prev, { title: 'Ny fas', body: 'Beskrivning' }]);
  };

  const deleteListItem = (index) => {
    setTimelineDraft((prev) => prev.filter((_, idx) => idx !== index));
  };

  const updateListItem = (index, field, value) => {
    setTimelineDraft((prev) => prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item)));
  };

  const saveTimeline = async () => {
    try {
      const payload = structuredClone(content || {});
      payload.timeline = timelineDraft;
      await saveContent(payload);
      setSaveStatus('Tidslinje sparad!');
      setEditingTimeline(false);
      setActiveEditor(false);
    } catch (_) {
      setSaveStatus('Fel vid sparande');
    } finally {
      setTimeout(() => setSaveStatus(null), 1600);
    }
  };

  if (loading && !content) return null;
  if (!timelineWithStep.length) return null;

  return (
    <section className={`about-timeline ${adminMode ? 'section-editable' : ''}`}>
      <div className="section-header about-timeline-header">
        <p className="eyebrow">Resan</p>
        <div className="section-header_row">
          <h2>Så här jobbar vi</h2>
          {adminMode && (
            <div className="edit-dock edit-dock--inline">
              <button className="about-edit-pen" onClick={openEditor}>
                <FaPen size={14} /> Redigera tidslinje
              </button>
            </div>
          )}
        </div>
        <p className="muted">Från första mötet till löpande samarbete</p>
      </div>
      <div className="timeline">
        {timelineWithStep.map((step, idx) => {
          const isExpanded = Boolean(expandedTimeline[idx]);

          return (
            <div className="timeline-card" key={`${step.title}-${idx}`}>
              <div className="step">0{step.step}</div>
              <h3>{step.title}</h3>
              <p className={`timeline-body ${!isExpanded ? 'timeline-body--collapsed' : ''}`}>{step.body}</p>
              <button
                className="timeline-read-more"
                type="button"
                onClick={() => toggleTimelineExpand(idx)}
                aria-label={isExpanded ? 'Dölj text' : 'Visa text'}
              >
                {isExpanded ? 'Visa mindre' : 'Läs mer'}
              </button>
            </div>
          );
        })}
      </div>

      {adminMode && editingTimeline && (
        <div
          className={`inline-edit ${activeEditor ? 'inline-edit--active' : ''}`}
          ref={editorRef}
        >
          {safeTimeline.map((step, idx) => (
            <div key={idx} className="inline-edit-stack timeline-edit-row">
              <input
                name={`timeline-title-${idx}`}
                value={step.title || ''}
                onChange={(e) => updateListItem(idx, 'title', e.target.value)}
                placeholder="Titel"
              />
              <textarea
                name={`timeline-body-${idx}`}
                value={step.body || ''}
                onChange={(e) => updateListItem(idx, 'body', e.target.value)}
                placeholder="Brödtext"
              />
              <button
                className="about-edit-pen ghost danger"
                onClick={() => deleteListItem(idx)}
                type="button"
              >
                Ta bort
              </button>
            </div>
          ))}

          <button
            className="about-edit-pen ghost"
            onClick={addListItem}
            type="button"
          >
            Lägg till fas
          </button>

          <div className="inline-edit-actions">
            <button className="about-edit-pen" type="button" onClick={saveTimeline} disabled={saving}>
              {saving ? 'Sparar...' : 'Spara'}
            </button>
            <button className="about-edit-pen ghost" type="button" onClick={cancelTimeline} disabled={saving}>
              Avbryt
            </button>
          </div>
        </div>
      )}

      {saveStatus && <p className="about-save-status">{saveStatus}</p>}
    </section>
  );
}

export default HomeTimelineSection;
